import * as React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Grid, TextField, Button, Select, MenuItem, InputLabel, FormControl, FormHelperText, Alert, Typography } from '@mui/material';
import FormPage from '../components/FormPage';
import { updateSalesNewInstallation, fetchSalesNewInstallation, fetchCustomers, fetchSolutions } from '../services/ApiService';

export function EditSale() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [haulierOptions, setHaulierOptions] = React.useState({});
  const [devicesOptions, setDevicesOptions] = React.useState({});
  const [document, setDocument] = React.useState(null);
  const [record, setRecord] = React.useState(location.state?.record || null);
  const [formData, setFormData] = React.useState(null);
  const [formErrors, setFormErrors] = React.useState({});

  React.useEffect(() => {
    fetchCustomers().then((res) => {
      setHaulierOptions(res.data.reduce((acc, c) => ({ ...acc, [c.id]: c.name }), {}));
    }).catch(() => {});
    fetchSolutions().then((res) => {
      setDevicesOptions(res.data.reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {}));
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!record) {
      fetchSalesNewInstallation(id).then((res) => setRecord(res.data)).catch(() => navigate('/sales-new-installation'));
    }
  }, [record, id]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (record) {
      setFormData({
        customer: record.customer || '',
        corporate: record.corporate || '',
        poc_name: record.poc_name || '',
        poc_contact: record.poc_contact || '',
        solution: record.solution || '',
        features: record.features || '',
        plan: record.plan || '',
        unit: record.unit || '',
        created_at: record.created_at,
        updated_at: record.updated_at,
      });
    }
  }, [record]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleDocumentChange = (e) => setDocument(e.target.files[0]);

  const handleCancel = () => navigate('/sales-new-installation');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key !== 'created_at' && key !== 'updated_at') payload.append(key, value);
      });
      if (document) payload.append('document', document);

      await updateSalesNewInstallation(id, payload);
      navigate('/sales-new-installation', { state: { successMessage: 'Sale updated successfully' } });
    } catch (error) {
      if (error.response?.data) setFormErrors(error.response.data);
      else setFormErrors({ general: error.message });
    }
  };

  if (!formData) {
    return (
      <FormPage title="Edit Sale" backTo="/sales-new-installation">
        <Typography>Loading...</Typography>
      </FormPage>
    );
  }

  return (
    <FormPage
      title="Edit Sale"
      backTo="/sales-new-installation"
      onSubmit={handleSubmit}
      actions={<>
        <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
        <Button variant="contained" type="submit">Update</Button>
      </>}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.75rem' }}>
        Last updated: {formData.updated_at?.slice(0, 19).replace('T', ' ')}
      </Typography>

      {formErrors.general && (
        <Alert severity="error" sx={{ mb: 2 }}>{formErrors.general}</Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={!!formErrors.customer}>
            <InputLabel>Customer</InputLabel>
            <Select name="customer" value={formData.customer} onChange={handleChange} label="Customer">
              {Object.entries(haulierOptions).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </Select>
            {formErrors.customer && <FormHelperText>{formErrors.customer}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={!!formErrors.solution}>
            <InputLabel>Solution</InputLabel>
            <Select name="solution" value={formData.solution} onChange={handleChange} label="Solution">
              {Object.entries(devicesOptions).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </Select>
            {formErrors.solution && <FormHelperText>{formErrors.solution}</FormHelperText>}
          </FormControl>
        </Grid>

        {[
          { name: 'corporate', label: 'Corporate', placeholder: 'e.g. Wafi / UEP / TPPL' },
          { name: 'poc_name', label: 'POC Name', placeholder: 'Point of contact name' },
          { name: 'poc_contact', label: 'POC Contact', placeholder: 'Phone number or email' },
          { name: 'unit', label: 'Unit', placeholder: 'e.g. Unit 1' },
        ].map((field) => (
          <Grid item xs={12} sm={6} key={field.name}>
            <TextField
              fullWidth required label={field.label} name={field.name} placeholder={field.placeholder}
              value={formData[field.name]} onChange={handleChange}
              error={!!formErrors[field.name]} helperText={formErrors[field.name]?.[0]}
            />
          </Grid>
        ))}

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={!!formErrors.plan}>
            <InputLabel>Plan</InputLabel>
            <Select name="plan" value={formData.plan} onChange={handleChange} label="Plan">
              <MenuItem value="New Install">New Install</MenuItem>
              <MenuItem value="Demo">Demo</MenuItem>
            </Select>
            {formErrors.plan && <FormHelperText>{formErrors.plan[0]}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth multiline rows={4} label="Features" name="features"
            placeholder="Describe the features included in this sale"
            value={formData.features} onChange={handleChange}
            error={!!formErrors.features} helperText={formErrors.features?.[0]}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>Upload Document</InputLabel>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt,image/*"
              name="document"
              onChange={handleDocumentChange}
              style={{ marginTop: 8 }}
            />
            <FormHelperText>Accepted formats: PDF, DOC, DOCX, TXT, or image files</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        Created: {formData.created_at?.slice(0, 19).replace('T', ' ')}
      </Typography>
    </FormPage>
  );
}
